/**
 * Settings → Receivables data: where the company's balances come from
 * (docs/tally/09). Feature branch only.
 *
 * While TallyPrime is a pilot, only companies Timely has switched on see it
 * (`connector_pilot`; the database enforces it at every step); every other
 * company sees the Google Sheet screen exactly as before.
 *
 * Two sources, side by side, neither second-class: TallyPrime (read directly,
 * through Timely Connector) and Google Sheets. One is the source at a time;
 * whichever it is feeds the same book, so Today, Customers and Reports never
 * differ by source. With neither connected, both are offered; with one
 * connected, its screen shows, and the other is one step away — a switch to
 * TallyPrime goes through the account review, a switch away from it starts
 * with disconnecting it.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge, Button, Card, cx, LoadingList, PageHeader } from '@timely/ui';
import { SourceFreshness } from '../receivables/SourceFreshness';
import { holds } from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { useDataSource } from '../datasource/useDataSource';
import { countTallyAccounts, useConnector } from '../datasource/useConnector';
import { TallyDataSource } from '../datasource/tally/TallyDataSource';
import { DataSource } from './DataSource';

type Choice = 'tally' | 'sheets';

export function ReceivablesData() {
    const context = useActiveWorkspace();
    const workspaceId = context.workspace.id;
    const canManage = holds(context, 'datasource.manage');
    const canSync = holds(context, 'datasource.sync');
    const [params, setParams] = useSearchParams();
    const chosen = (params.get('source') as Choice | null) ?? null;
    const choose = (next: Choice | null) => {
        const p = new URLSearchParams(params);
        if (next) p.set('source', next); else p.delete('source');
        setParams(p, { replace: true });
    };

    const [waitingForPairing, setWaitingForPairing] = useState(false);
    const [pollMs, setPollMs] = useState<number | null>(null);
    const connector = useConnector(workspaceId, pollMs);
    const sheet = useDataSource(workspaceId);
    const status = connector.status;
    const tallyLive = status !== null && status.status !== 'revoked';
    const sheetConnected = sheet.integration.spreadsheetId !== null;

    // Re-read often while the connector moves the setup on; less often once the figures are on screen for
    // the owner to check (a large company's preview is most of a megabyte); now and then once it is the source.
    const previewShown = status?.status === 'pending' && Boolean(status.preview);
    useEffect(() => {
        setPollMs(previewShown ? 15_000 : status?.status === 'pending' || (waitingForPairing && !tallyLive) ? 3_000 : status?.status === 'active' ? 30_000 : null);
    }, [status?.status, previewShown, waitingForPairing, tallyLive]);

    // After TallyPrime, accounts carry TallyPrime identities a sheet would not match.
    const [tallyAccounts, setTallyAccounts] = useState(0);
    useEffect(() => {
        if (tallyLive || sheetConnected) return;
        void countTallyAccounts(workspaceId).then(setTallyAccounts);
    }, [workspaceId, tallyLive, sheetConnected, status?.status]);

    if (connector.loading || sheet.loading) {
        return (
            <Page width="standard">
                <PageHeader title="Receivables data" subtitle="Where your customers’ balances come from." />
                <Card className="overflow-hidden"><LoadingList label="Loading your receivables data" rows={3} /></Card>
            </Page>
        );
    }

    // Outside the TallyPrime pilot (Timely's switch, server-side), the screen is the Google Sheet's alone, exactly as
    // before TallyPrime. A connection that already exists still shows, so it can be seen and disconnected.
    if (!connector.pilot && !tallyLive) return <DataSource />;

    // TallyPrime is the source, being set up, or chosen — first: choosing it must work while a sheet is still the source.
    if (tallyLive || chosen === 'tally') {
        return (
            <Page width="standard">
                <PageHeader title="Receivables data" subtitle="TallyPrime, read directly by Timely Connector on your computer. Timely reads it; it never writes to it." />
                {tallyLive && <SourceFreshness withLink={false} className="-mt-2 mb-4" />}
                {connector.error && <p role="alert" className="mb-4 text-body text-dang">{connector.error}</p>}
                <TallyDataSource workspaceId={workspaceId} status={status} sheetConnected={sheetConnected} canManage={canManage} canSync={canSync}
                    reload={connector.reload} onWaitingForPairing={setWaitingForPairing} />
                {!tallyLive && (
                    <button type="button" className="mt-5 text-secondary text-accent-text font-semibold hover:underline" onClick={() => choose(null)}>
                        {sheetConnected ? '← Back to your Google Sheet' : '← Choose a different source'}
                    </button>
                )}
                {status?.status === 'pending' && sheetConnected && (
                    <SourceCard kind="sheets" className="mt-5">
                        Your Google Sheet stays the source until you connect TallyPrime.
                    </SourceCard>
                )}
                {/* A note about the other source, not a card for a choice that cannot be made from here. */}
                {status?.status === 'active' && (
                    <p className="mt-6 text-secondary text-label-3">
                        <span className="font-semibold text-label-2">Google Sheets.</span>{' '}
                        TallyPrime is your receivables source. To read from a Google Sheet instead, disconnect TallyPrime first.
                    </p>
                )}
            </Page>
        );
    }

    // The Google Sheet is the source (or the owner chose to set one up): its own screen, with TallyPrime one step away.
    if (sheetConnected || chosen === 'sheets') {
        return (
            <DataSource
                sourceSwitcher={canManage ? (
                    sheetConnected ? (
                        <SourceCard kind="tally" action={<Button variant="secondary" onClick={() => choose('tally')}>Switch to TallyPrime</Button>}>
                            Your accounts are matched before anything changes, and keep all of their history.
                        </SourceCard>
                    ) : (
                        <button type="button" className="mt-5 text-secondary text-accent-text font-semibold hover:underline" onClick={() => choose(null)}>← Choose a different source</button>
                    )
                ) : undefined}
            />
        );
    }

    // Neither: the choice.
    return (
        <Page width="standard">
            <PageHeader title="Receivables data" subtitle="Where your customers’ balances come from. Timely Payment reads them; it never writes to your source." />
            <SourceFreshness withLink={false} className="-mt-2 mb-4" />
            {status?.status === 'revoked' && (
                <Card className="p-5 mb-5">
                    <div className="flex items-center gap-3"><h3 className="text-heading font-semibold text-label">TallyPrime</h3><Badge variant="text" dot>Disconnected</Badge></div>
                    <p className="mt-1 text-body text-label-2">Your accounts keep their last figures and all of their history. Nothing updates them until a source is connected again.</p>
                </Card>
            )}
            <div role="group" aria-label="Choose where your receivables come from" className="grid gap-4 md:grid-cols-2">
                <SourceCard kind="tally" action={canManage ? <Button variant="primary" onClick={() => choose('tally')}>Connect TallyPrime</Button> : undefined}>
                    Connect directly to TallyPrime and keep your receivables automatically updated.
                </SourceCard>
                <SourceCard kind="sheets" action={canManage ? (
                    <Button variant="secondary" disabled={tallyAccounts > 0} onClick={() => choose('sheets')}>Connect a Google Sheet</Button>
                ) : undefined}>
                    Connect an existing private Google Sheet.
                    {tallyAccounts > 0 && (
                        <span className="block mt-2 text-secondary text-label-3">
                            Your {tallyAccounts} account{tallyAccounts === 1 ? '' : 's'} came from TallyPrime. Moving them to a Google Sheet needs account matching in that direction,
                            which is not available yet; connect TallyPrime again, or ask Timely support.
                        </span>
                    )}
                </SourceCard>
            </div>
            {!canManage && <p className="mt-4 text-secondary text-label-3">Somebody who manages receivables data can connect a source.</p>}
        </Page>
    );
}

function SourceCard({ kind, action, className, children }: { kind: Choice; action?: React.ReactNode; className?: string; children: React.ReactNode }) {
    return (
        <Card className={cx('p-5 flex flex-col gap-3', className)}>
            <h3 className="text-heading font-semibold text-label">{kind === 'tally' ? 'TallyPrime' : 'Google Sheets'}</h3>
            <p className="text-body text-label-2 grow">{children}</p>
            {action && <div>{action}</div>}
        </Card>
    );
}
